import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import MaterialTable, { MTableToolbar } from 'material-table';
import moment from 'moment';
import 'moment/locale/th';
import { uniqueId, uniqBy } from 'lodash';

import { useTranslation } from 'react-i18next';

// ui
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import NativeSelect from '@material-ui/core/NativeSelect';
import FlagIcon from '@material-ui/icons/Flag';

// icon
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';

// style
import styles from '../style';
import { StyledIconButton } from '../../../../components/button/Button';
import tableIcons from '../../../../components/table/MaterialTableIcon';

// Utils
import exportCsv from '../../../../utils/export/Csv';

import WaterLevelColor from '../../../../components/color/waterlevel/WaterLevelColor';
import WaterLevelStatusColor from '../../../../components/color/waterlevel/waterLevelStatusColor';
import WaterLevelModal from '../../../../components/modal/Modal';
import WaterLevelChart from '../../chart/WaterLevelChart';

const useStyles = makeStyles(styles);

export default function WaterLavelDatalists(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    datas, handleSearchMarker, currentMode, currentArea, userIsLogin,
  } = props;
  const [wlData, setWlData] = useState([]);
  const [agencyData, setAgencyData] = useState([]);
  const [selectAgency, setSelectAgency] = useState('');
  const [isKeyStation, SetIsKeyStation] = useState(true);
  const [selectSituationLevel, setSituationLevel] = useState('');
  const [modalData, setModalData] = useState({
    open: false,
    header: t('กราฟระดับน้ำ'),
    subheader: null,
    content: null,
  });

  const isExport = (userIsLogin) ? { csv: true } : userIsLogin;

  useEffect(() => {
    setModalData({
      open: false,
      header: '',
      subheader: null,
      content: null,
    });

    let agency = datas.map((d) => ({
      id: d.agency.id,
      name: d.agency.agency_name.th,
    }));
    agency = uniqBy(agency.sort((a, b) => a.name.localeCompare(b.name, 'th')), 'id');
    setAgencyData(agency);

    setWlData(datas);
  }, [datas]);

  // force modal close
  useEffect(() => {
    if (modalData.open) modalData.open = false;
  }, [modalData.open]);

  function filterData(currentData, mode, area) {
    let filterD = [];

    if (mode === 'province' && area.code !== '0') {
      filterD = wlData.filter((d) => d.geocode.province_code === area.code);
    } else if (mode === 'basin' && area.code !== '0') {
      filterD = wlData.filter((d) => d.basin.basin_code === parseInt(area.code, 10));
    } else {
      filterD = currentData;
    }

    return filterD;
  }

  const handleAgencyChange = (event) => {
    const selectVar = event.target.value;
    const agencyFilter = (selectVar === '') ? datas : datas.filter((d) => (d.agency.id === parseInt(selectVar, 10)));
    setSelectAgency(selectVar);
    setWlData(agencyFilter);
  };

  const handleSituationChange = (event) => {
    const selectVar = event.target.value;
    // console.log("situation level ", selectVar);
    const dataFilter = (selectVar === '') ? datas : datas.filter((d) => (d.situation_level === parseInt(selectVar, 10)));
    setSituationLevel(selectVar);
    setWlData(dataFilter);
  };

  function filterKeyStation() {
    setWlData(datas.filter((d) => (d.station.is_key_station === isKeyStation)));
  }

  const handleChart = (data) => (e) => {
    e.preventDefault();

    setModalData({
      open: true,
      header: t('กราฟระดับน้ำ'),
      subheader: null,
      content: <WaterLevelChart id={data.station.id} provinceId={data.geocode.province_code} />,
    });
  };

  const Modal = React.memo((p) => (
    <WaterLevelModal
      open={p.modalData.open}
      modalHeader={p.modalData.header}
      modalSubHeader={p.modalData.subheader}
      modalContent={p.modalData.content}
      modalStyle={p.modalData.style}
    />
  ));

  return (
    <>
      <Paper className={classes.root}>
        <MaterialTable
          icons={tableIcons}
          columns={[
            {
              title: t('ชื่อสถานี'),
              title_export: t('ชื่อสถานี'),
              field: 'station.tele_station_name.th',
              width: 250,
              headerStyle: {
                left: 0,
                position: 'sticky',
                zIndex: 50,
              },
              cellStyle: {
                left: 0,
                position: 'sticky',
                // wordWrap: 'break-word',
                // whiteSpace: 'pre-wrap',
                textAlign: 'left',
                padding: '0em',
              },
              render: (row) => (
                <Box display="flex" style={{ backgroundColor: 'white' }}>
                  <Button
                    style={{ textAlign: 'left' }}
                    onClick={handleSearchMarker(row.id,
                      row.station.tele_station_lat, row.station.tele_station_long)}
                    title={`${row.agency.agency_name.th}\n${t('แสดงตำแหน่งบนแผนที่')}`}
                  >
                    {`${row.station.tele_station_oldcode} ${row.station.tele_station_name.th}`}
                  </Button>
                </Box>
              ),
              customSort: (a, b) => a.station.tele_station_name.th.localeCompare(b.station.tele_station_name.th, 'th'),
            },
            {
              title_export: t('ตำบล'),
              type: 'string',
              field: 'geocode.tumbon_name.th',
              hidden: true,
            },
            {
              title_export: t('อำเภอ'),
              type: 'string',
              field: 'geocode.amphoe_name.th',
              hidden: true,
            },
            {
              title: t('ที่ตั้ง'),
              title_export: t('จังหวัด'),
              field: 'geocode.province_name.th',
              type: 'string',
              width: 300,
              render: (row) => `ต.${row.geocode.tumbon_name.th} อ.${row.geocode.amphoe_name.th} จ.${row.geocode.province_name.th}`,
            },
            {
              title: t('ลุ่มน้ำ'),
              title_export: t('ลุ่มน้ำ'),
              type: 'string',
              field: 'basin.basin_name.th',
              width: 200,
              hidden: !userIsLogin,
            },
            {
              title: t('เวลา'),
              title_export: t('วันที่'),
              field: 'waterlevel_datetime',
              type: 'date',
              width: 80,
              render: (row) => `${moment(row.waterlevel_datetime).format('HH:mm')} น.`,
            },
            {
              title:
  <>
    {t('ระดับน้ำ')}
    <br />
    <Typography variant="caption" display="block">
      (
      {t('ม.รทก.')}
      )
    </Typography>
  </>,
              title_export: `${t('ระดับน้ำ')} (${t('ม.รทก.')})`,
              field: 'waterlevel_msl',
              type: 'numeric',
              width: 80,
              render: (row) => `${row.waterlevel_msl}`,
            },
            {
              title:
  <>
    {t('ระดับตลิ่ง')}
    <br />
    <Typography variant="caption" display="block">
      (
      {t('ม.รทก.')}
      )
    </Typography>
  </>,
              title_export: `${t('ระดับตลิ่ง')} (${t('ม.รทก.')})`,
              field: 'bank',
              type: 'numeric',
              width: 100,
              export: false,
              // render: (row) => row.station.min_bank.toFixed(2),
              render: (row) => {
                let bank = '-';
                if (row.station.min_bank != null) {
                  bank = parseFloat(row.station.min_bank).toFixed(2);
                } else if (row.station.left_bank !== null
                  && row.station.right_bank !== null) {
                  bank = parseFloat(Math.min(row.station.left_bank,
                    row.station.right_bank)).toFixed(2);
                }
                return (
                  <>
                    {bank}
                  </>
                );
              },
            },
            {
              title: t('ระดับตลิ่งซ้าย'),
              title_export: `${t('ระดับตลิ่งซ้าย')} (${t('ม.รทก.')})`,
              field: 'station.left_bank',
              type: 'numeric',
              hidden: true,
            },
            {
              title: t('ระดับตลิ่งขวา'),
              title_export: `${t('ระดับตลิ่งขวา')} (${t('ม.รทก.')})`,
              field: 'station.right_bank',
              type: 'numeric',
              hidden: true,
            },
            {
              title:
  <>
    {t('ค่าศูนย์เสาระดับ')}
    <br />
    <Typography variant="caption" display="block">
      (
      {t('ม.รทก.')}
      )
    </Typography>
  </>,
              title_export: `${t('ค่าศูนย์เสาระดับ')} (${t('ม.รทก.')})`,
              field: 'bank',
              type: 'numeric',
              width: 100,
              export: false,
              render: (row) => (
                <>
                  {row.station.offset !== null ? parseFloat(row.station.offset).toFixed(2) : '-'}
                </>
              ),
            },
            {
              title: t('หน่วยงาน'),
              title_export: t('หน่วยงาน'),
              field: 'agency.agency_name.th',
              type: 'string',
              hidden: true,
              export: true,
            },
            {
              title: t('รหัสสถานี'),
              title_export: t('รหัสสถานี'),
              field: 'station.tele_station_oldcode',
              type: 'string',
              hidden: true,
              export: true,
            },
            {
              title_export: 'Latitude',
              type: 'string',
              field: 'station.tele_station_lat',
              hidden: true,
              export: true,
            },
            {
              title_export: 'Longitude',
              type: 'string',
              field: 'station.tele_station_long',
              hidden: true,
              export: true,
            },
            // {
            //   title: 'สถานีหลักแห่งชาติ',
            //   title_export: ' สถานีหลักแห่งชาติ',
            //   field: 'station.tele_station_lat.is_key_station',
            //   type: 'string',
            //   hidden: true,
            //   export: false,
            // },
            {
              title: `% ${t('ความจุลำน้ำ')}`,
              title_export: `% ${t('ความจุลำน้ำ')}`,
              field: 'storage_percent',
              type: 'numeric',
              width: 100,
              // export: false,
              render: (row) => {
                // const userStoragePercent = (row.storage_percent < 0) ? '-' : row.storage_percent;
                // const storagePercent = (userIsLogin) ? row.storage_percent : userStoragePercent;

                const userStoragePercent = ((row.storage_percent < 0) || (row.storage_percent === null)) ? '-' : row.storage_percent;
                const storagePercent = userStoragePercent;

                return (
                  `${storagePercent}`
                );
              },
            },
            {
              title: `${t('สถานการณ์')}`,
              title_export: `${t('สถานการณ์')}`,
              field: 'situation',
              type: 'string',
              width: 115,
              export: false,
              render: (row) => {
                const status = WaterLevelColor(row.storage_percent);

                return (
                  <Box
                    borderRadius={3}
                    style={{
                      align: 'center',
                      textAlign: 'center',
                      margin: '0 auto',
                      width: '50px',
                      backgroundColor: status.color.statusColor,
                      color: status.color.constrastColor,
                    }}
                  >
                    {status.color.label}
                  </Box>
                );
              },
            },
            {
              title: '',
              field: '',
              sorting: false,
              width: 115,
              render: (row) => (
                <>
                  <Tooltip title={t('คลิ๊กเพื่อแสดงกราฟ')}>
                    <StyledIconButton onClick={handleChart(row)}>
                      <FontAwesomeIcon icon={faChartLine} style={{ color: '#888' }} />
                    </StyledIconButton>
                  </Tooltip>
                </>
              ),
            },

          ]}
          data={filterData(wlData, currentMode, currentArea)}
          localization={{
            toolbar: {
              searchPlaceholder: t('ค้นหา'),
              exportTitle: t('ดาวน์โหลดข้อมูล ระดับน้ำ'),
            },
            pagination: {
              labelRowsSelect: t('แถว'),
            },
            body: { emptyDataSourceMessage: wlData.length === 0 && selectSituationLevel === '' ? <h3>{t('กำลังโหลดข้อมูล...')}</h3> : <h3>{t('ไม่มีข้อมูลตามเงื่อนไข')}</h3> },
          }}
          options={{
            // tableLayout: 'fixed',
            pageSize: 10,
            sorting: true,
            // searchFieldStyle: {
            //   width: '50%',
            //   alignSelf: { sm: 'center', md:'flex-end' },
            // },
            rowStyle: {
              wordWrap: 'break-word',
            },
            toolbar: true,
            showTitle: false,
            search: true,
            exportButton: isExport,
            exportCsv,
            cellStyle: {
              padding: 8,
            },
          }}
          components={{
            Toolbar: (props) => (
              <Box mx={2}>
                <Grid container direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
                  <Grid item xs={12} sm={8} md={6}>
                    <MTableToolbar {...props} />
                  </Grid>
                </Grid>
                <Grid container direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
                  <Grid item xs="auto" sm="auto" md="auto">
                    <FormControl className={classes.formControl}>
                      <Box fontFamily="Kanit">
                        {t('หน่วยงาน')}
                      </Box>
                    </FormControl>
                  </Grid>
                  <Grid item xs={8} sm={6} md="auto">
                    <FormControl className={classes.formControl}>
                      <NativeSelect
                        id="agency-select"
                        value={selectAgency}
                        onChange={handleAgencyChange}
                      >
                        <option value="">{t('ทุกหน่วยงาน')}</option>
                        {agencyData.map((val) => (
                          <option key={uniqueId()} value={val.id}>{val.name}</option>
                        ))}
                      </NativeSelect>
                    </FormControl>
                  </Grid>
                  <Grid item xs="auto" sm="auto" md="auto">
                    <FormControl className={classes.formControl}>
                      <Box fontFamily="Kanit">
                        {t('สถานการณ์')}
                      </Box>
                    </FormControl>
                  </Grid>
                  <Grid item xs={8} sm={6} md="auto">
                    <FormControl className={classes.formControl}>
                      <NativeSelect
                        id="agency-select"
                        value={selectSituationLevel}
                        onChange={handleSituationChange}
                      >
                        <option value="">{t('ทุกสถานการณ์')}</option>
                        {WaterLevelStatusColor.slice(1).map((val) => (
                          <option key={uniqueId()} value={val.level}>{val.label}</option>
                        ))}
                      </NativeSelect>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={3} md="auto">
                    <FormControl className={classes.formControl}>
                      <Button
                        id="button-key-station"
                        variant="outlined"
                        size="small"
                        color={isKeyStation ? 'default' : 'primary'}
                        className={classes.button}
                        startIcon={<FlagIcon />}
                        onClick={() => {
                          SetIsKeyStation(!isKeyStation);
                          filterKeyStation();
                        }}
                      >
                        {t('สถานีหลัก')}
                      </Button>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            ),
          }}
        />

        <Modal modalData={modalData} />
      </Paper>
    </>
  );
}

WaterLavelDatalists.propTypes = {
  datas: PropTypes.instanceOf(Array).isRequired,
  userIsLogin: PropTypes.bool.isRequired,
  handleSearchMarker: PropTypes.func.isRequired,
  currentMode: PropTypes.string.isRequired,
  currentArea: PropTypes.instanceOf(Object).isRequired,
};
